import { makeStyles } from "@material-ui/core/styles";

const useCrudListStyles = makeStyles((theme) => ({
  pageRoot: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: "100%",
    minWidth: 0,
    backgroundColor: theme.palette.background.default,
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    width: "100%",
    flexShrink: 0,
    overflow: "visible",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1.5),
    },
  },
  pageTitle: {
    fontWeight: 600,
    fontSize: "1.5rem",
    lineHeight: 1.3,
    letterSpacing: "-0.02em",
    color: theme.palette.text.primary,
    margin: 0,
    flex: "0 0 auto",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginLeft: "auto",
    flex: "1 1 420px",
    justifyContent: "flex-end",
    overflow: "visible",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      width: "100%",
      flex: "1 1 100%",
      justifyContent: "stretch",
    },
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  toolbarItem: {
    display: "flex",
    alignItems: "center",
    flex: "0 0 auto",
    minHeight: 40,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  tagsFilterField: {
    minWidth: 200,
    flex: "1 1 220px",
    maxWidth: 280,
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "none",
      flex: "1 1 100%",
    },
    "& .MuiAutocomplete-root": {
      width: "100%",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
      minHeight: 40,
      paddingTop: 2,
      paddingBottom: 2,
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 14px",
    },
  },
  toolbarWide: {
    flex: "1 1 480px",
  },
  searchField: {
    minWidth: 220,
    flex: "1 1 260px",
    maxWidth: 360,
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "none",
      flex: "1 1 100%",
      minWidth: 0,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
      minHeight: 40,
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
    },
  },
  pageSizeSelect: {
    minWidth: 165,
    flex: "0 0 auto",
    overflow: "visible",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
      minHeight: 40,
    },
    "& .MuiInputLabel-outlined": {
      backgroundColor: theme.palette.background.default,
      padding: "0 4px",
    },
  },
  primaryButton: {
    borderRadius: 8,
    minHeight: 40,
    padding: theme.spacing(1, 2.5),
    whiteSpace: "nowrap",
    boxShadow: "none",
    flex: "0 0 auto",
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "auto",
    },
  },
  secondaryButton: {
    borderRadius: 8,
    minHeight: 40,
    padding: theme.spacing(1, 2),
    whiteSpace: "nowrap",
    flex: "0 0 auto",
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "auto",
    },
  },
  mainPaper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    minWidth: 0,
    padding: 0,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow:
      theme.palette.type === "light"
        ? "0 1px 2px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.1)"
        : "0 1px 2px rgba(0, 0, 0, 0.24)",
    overflow: "hidden",
  },
  tableScroll: {
    flex: 1,
    overflow: "auto",
    ...theme.scrollbarStyles,
  },
  table: {
    minWidth: ({ tableMinWidth }) => tableMinWidth,
    "& .MuiTableCell-head": {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.secondary,
      fontWeight: 600,
      fontSize: "0.8125rem",
      letterSpacing: "0.01em",
      textTransform: "none",
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.5, 2),
      whiteSpace: "nowrap",
    },
    "& .MuiTableCell-body": {
      padding: theme.spacing(1.5, 2),
      color: theme.palette.text.primary,
      fontSize: "0.875rem",
      borderBottom: `1px solid ${theme.palette.divider}`,
      height: 52,
      verticalAlign: "middle",
    },
    "& .MuiTableRow-root:last-child .MuiTableCell-body": {
      borderBottom: "none",
    },
    "& .MuiTableRow-root:hover .MuiTableCell-body": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  actionButton: {
    padding: 8,
    color: theme.palette.text.secondary,
    borderRadius: 8,
    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.action.hover,
    },
    "&.Mui-disabled": {
      opacity: 0.35,
    },
  },
  actionButtonDanger: {
    "&:hover": {
      color: theme.palette.error.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  loadMoreWrap: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  loadingText: {
    marginLeft: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  loadingRow: {
    "& .MuiTableCell-body": {
      borderBottom: "none",
    },
  },
  actionsCell: {
    whiteSpace: "nowrap",
  },
}));

export default useCrudListStyles;
